/**
 * Brands admin (P4 / spec 017 Feature D, pre-build audit §G.3) — the cross-customer
 * brand MANAGEMENT table. ADMIN-only; mounted in the Editor's "Brands" tab.
 *
 * Replaces the old pending-only review queue (BrandApproval) with a full table:
 * every brand across every in-scope customer, attributed to its owner (email joined
 * from listCustomers), filterable by status, with per-state row actions
 * (approve/reject/revoke/reassign/delete) and an admin "Create brand" form.
 *
 * Locked decisions: revoke → `draft`; admin-created brands are born `approved`
 * (reassignable later). Uses only the injected JsonClient via useServices().
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { serverTsDate } from "@genz/platform";
import type { BrandProfile, BrandStatus, CustomerRecord } from "@genz/platform";
import {
  BRAND_STATUS_BADGE, BRAND_STATUS_FILTERS, BRAND_STATUS_LABEL, brandFilterLabel,
  type BrandStatusFilter,
} from "./brandStatus.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { RowAction } from "../ui/RowAction.js";
import { Icon } from "../ui/Icon.js";
import { BrandSwatch } from "./BrandSwatch.js";
import { toast } from "../ui/Toast.js";
import "./branding.css";

// WS-B B-10 / MIN-7: ONE brand-status vocabulary — the labels, the tones and the FILTER
// ORDER. These two pages each kept a private copy, and their orders had drifted.
const STATUS_BADGE = BRAND_STATUS_BADGE;
const STATUS_LABEL = BRAND_STATUS_LABEL;
type StatusFilter = BrandStatusFilter;
const FILTERS = BRAND_STATUS_FILTERS;

/** Records without a status (older brands) are treated as approved. */
function statusOf(b: BrandProfile): BrandStatus {
  return b.status ?? "approved";
}

/** Human date for submitted/reviewed timestamps; "—" when missing/invalid. */
function fmtDate(iso?: string | null): string {
  const d = serverTsDate(iso);
  return d ? d.toLocaleDateString() : "—";
}

export function BrandsAdmin() {
  const { client } = useServices();
  const [brands, setBrands] = useState<BrandProfile[] | null>(null);
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [bs, cs] = await Promise.all([client.adminListBrands("all"), client.listCustomers()]);
      setBrands(bs);
      setCustomers(cs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const emailOf = useMemo(() => {
    const map = new Map(customers.map((c) => [c.id, c.email]));
    return (id: string | null | undefined) => (id ? map.get(id) ?? id : "—");
  }, [customers]);

  /** `done` is what the operator is told when it worked. On a CROSS-CUSTOMER table with a
   *  Delete button, a mutation whose only feedback is the table re-rendering is the one an
   *  admin cannot be sure hit the row they meant. */
  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      if (done) toast(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // The admin surface searches across the brand AND its OWNER — which is the difference
  // between the two pages: a customer looks for their own brand by name, an admin looks for
  // "everything belonging to this account". Same data layer, different job (Rule B).
  const rows = (brands ?? []).filter((b) => {
    if (filter !== "all" && statusOf(b) !== filter) return false;
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return b.name.toLowerCase().includes(needle)
      || String(emailOf(b.customer_id)).toLowerCase().includes(needle);
  });

  return (
    <section className="brands-admin" data-testid="brands-admin">
      <div className="brands-admin__bar">
        <label className="brands-admin__filter brands-admin__filter--search">
          Search
          <input
            type="search"
            value={search}
            placeholder="Brand or owner"
            data-testid="brand-admin-search"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="brands-admin__filter">
          Status
          <select
            value={filter}
            disabled={busy}
            data-testid="brand-admin-filter"
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
          >
            {FILTERS.map((f) => (
              <option key={f} value={f}>{brandFilterLabel(f)}</option>
            ))}
          </select>
        </label>
        {/* ONE primary on this surface, and it moves rather than multiplying: while the
            create row is closed the page's one invitation is to open it; while it is open
            the commit inside it is the invitation and this becomes the way out. Three
            `.btn--primary` in one viewport is what the inventory found here. */}
        <button
          type="button"
          className={`btn btn--sm${creating ? " btn--ghost" : " btn--primary"}`}
          disabled={busy}
          data-testid="brand-admin-create-toggle"
          onClick={() => setCreating((v) => !v)}
        >
          {creating ? "Cancel" : <><Icon name="plus" size={16} /> Create brand</>}
        </button>
      </div>

      {error && <p role="alert" data-testid="brands-admin-error">{error}</p>}

      {creating && (
        <BrandCreateForm
          customers={customers}
          busy={busy}
          onCreate={(input) => run(async () => {
            await client.adminCreateBrand(input);
            setCreating(false);
          }, `${input.name} created for ${emailOf(input.customerId)}`)}
        />
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon="brands"
          title={(brands ?? []).length === 0 ? "No brands yet" : "No brands match"}
          hint={(brands ?? []).length === 0
            ? "Brands created by customers land here for review; you can also create one for an account."
            : "No brand matches this search and status. Clear them to see the rest."}
          testId="brands-admin-empty"
        />
      ) : (
        <div className="table-scroll">
        <table className="table brands-admin__table" data-testid="brands-admin-table">
          <thead>
            <tr><th>Brand</th><th>Customer</th><th>Status</th><th>Review</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((b) => {
              const st = statusOf(b);
              return (
                <tr key={b.id} data-testid="brand-admin-row">
                  <td>
                    <span className="brands__status">
                      <strong>{b.name}</strong>
                      {b.color_primary && <BrandSwatch color={b.color_primary} />}
                      {b.color_secondary && <BrandSwatch color={b.color_secondary} />}
                    </span>
                  </td>
                  <td data-testid="brand-admin-owner">{emailOf(b.customer_id)}</td>
                  <td>
                    <span
                      className={`badge ${STATUS_BADGE[st]}`}
                      data-testid="brand-admin-status"
                    >
                      {STATUS_LABEL[st]}
                    </span>
                  </td>
                  <td className="faint" data-testid="brand-admin-review">
                    {st === "pending" && <>Submitted {fmtDate(b.submitted_at)}</>}
                    {(st === "approved" || st === "rejected") && b.reviewed_by && (
                      <>By {emailOf(b.reviewed_by)}</>
                    )}
                    {st === "rejected" && b.rejection_reason && (
                      <span className="brands-admin__reason" data-testid="brand-admin-reason">
                        {" — "}{b.rejection_reason}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row">
                      {st === "pending" && (
                        <>
                          {/* Order: Reject (secondary) then Approve (accent primary). */}
                          <RejectControl
                            brand={b}
                            busy={busy}
                            onReject={(reason) => run(() => client.rejectBrand(b.id, reason), `${b.name} rejected`)}
                          />
                          <RowAction
                            // A ROW action, repeated once per pending brand. As `--primary`
                            // it painted a volt fill on every row of the queue — the same
                            // defect K2-20 fixed on the customer page's "Submit for approval".
                            // WS-Z9: and now it does not print the word either.
                            label="Approve" icon="check" disabled={busy}
                            data-testid="brand-admin-approve"
                            onClick={() => void run(() => client.approveBrand(b.id), `${b.name} approved`)}
                          />
                        </>
                      )}
                      {st === "approved" && (
                        <RowAction
                          label="Revoke" icon="undo" tone="quiet" disabled={busy}
                          data-testid="brand-admin-revoke"
                          onClick={() => void run(() => client.revokeBrand(b.id), `${b.name} revoked — back to Draft`)}
                        />
                      )}
                      <select
                        className="brands-admin__assign"
                        value=""
                        disabled={busy}
                        data-testid={`brand-admin-assign-${b.id}`}
                        aria-label={`Reassign ${b.name}`}
                        onChange={(e) => {
                          const cid = e.target.value;
                          if (cid) void run(() => client.adminAssignBrand(b.id, cid), `${b.name} reassigned to ${emailOf(cid)}`);
                        }}
                      >
                        <option value="">Reassign…</option>
                        {customers.map((c) => (
                          <option key={c.id} value={c.id}>{c.email}</option>
                        ))}
                      </select>
                      {/* Delete asks first. A brand delete is irreversible and used to fire on a
                          single click. In-app `ConfirmPopover`, not `window.confirm`: the native
                          dialog is Tauri-flaky, and a falsy return would make Delete do nothing. */}
                      <ConfirmPopover
                        message={`Delete the brand “${b.name}”? This cannot be undone.`}
                        confirmLabel="Delete brand"
                        danger
                        onConfirm={() => void run(() => client.adminDeleteBrand(b.id), `${b.name} deleted`)}
                      >
                        {(toggle) => (
                          <RowAction
                            label="Delete" icon="trash" tone="danger" disabled={busy}
                            data-testid="brand-admin-delete"
                            onClick={toggle}
                          />
                        )}
                      </ConfirmPopover>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

/** Reject affordance (item 9): the "Reject" button reveals an optional reason field;
 *  confirming calls rejectBrand(id, reason?). The reason is stored and shown to the
 *  owner via BrandProfile.rejection_reason. */
function RejectControl({ brand, busy, onReject }: {
  brand: BrandProfile;
  busy: boolean;
  onReject: (reason?: string) => Promise<unknown> | void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (!open) {
    return (
      <RowAction
        label="Reject" icon="close" tone="quiet" disabled={busy}
        data-testid="brand-admin-reject"
        onClick={() => setOpen(true)}
      />
    );
  }
  return (
    <span className="brands-admin__reject">
      <input
        type="text"
        placeholder="Reason (optional)"
        value={reason}
        disabled={busy}
        data-testid="brand-admin-reject-reason"
        aria-label={`Rejection reason for ${brand.name}`}
        onChange={(e) => setReason(e.target.value)}
      />
      <button
        type="button" className="btn btn--sm btn--danger" disabled={busy}
        data-testid="brand-admin-reject-confirm"
        onClick={() => void onReject(reason.trim() || undefined)}
      >Confirm reject</button>
    </span>
  );
}

/** Admin "Create brand" form: pick a customer + name + colors → adminCreateBrand. */
function BrandCreateForm({ customers, busy, onCreate }: {
  customers: CustomerRecord[];
  busy: boolean;
  onCreate: (input: {
    customerId: string; name: string; colorPrimary?: string; colorSecondary?: string;
  }) => void;
}) {
  const [customerId, setCustomerId] = useState("");
  const [name, setName] = useState("");
  const [colorPrimary, setColorPrimary] = useState("");
  const [colorSecondary, setColorSecondary] = useState("");

  return (
    <form
      className="brands-admin__create"
      data-testid="brand-admin-create-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!customerId || !name.trim()) return;
        onCreate({
          customerId,
          name: name.trim(),
          ...(colorPrimary ? { colorPrimary } : {}),
          ...(colorSecondary ? { colorSecondary } : {}),
        });
      }}
    >
      <select
        value={customerId}
        disabled={busy}
        data-testid="brand-admin-create-customer"
        aria-label="Customer"
        onChange={(e) => setCustomerId(e.target.value)}
      >
        <option value="">Select customer…</option>
        {customers.map((c) => (
          <option key={c.id} value={c.id}>{c.email}</option>
        ))}
      </select>
      <input
        type="text" placeholder="Brand name" value={name} disabled={busy}
        data-testid="brand-admin-create-name"
        onChange={(e) => setName(e.target.value)}
      />
      <input
        type="text" placeholder="#primary" value={colorPrimary} disabled={busy}
        data-testid="brand-admin-create-color"
        onChange={(e) => setColorPrimary(e.target.value)}
      />
      <input
        type="text" placeholder="#secondary" value={colorSecondary} disabled={busy}
        data-testid="brand-admin-create-color2"
        onChange={(e) => setColorSecondary(e.target.value)}
      />
      <button
        type="submit" className="btn btn--sm btn--primary" disabled={busy}
        data-testid="brand-admin-create-submit"
      >Create</button>
    </form>
  );
}
